import { useEffect, useState } from "react";
import PopupView from "../../../elements/popupview";
import styled from "styled-components";
import { getData } from "../../../../backend/api";
import { dateFormat, dateTimeFormat } from "../../../functions/date";
import {
  FiEye,
  FiEdit,
  FiUserPlus,
  FiUserCheck,
  FiClock,
  FiMessageSquare,
  FiFileText,
  FiLock,
  FiUnlock,
  FiRefreshCw
} from "react-icons/fi";


const TimelineContainer = styled.div`
  max-height: 70vh;
  overflow-y: auto;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: flex-start;
  position: relative;
//   padding-left: 30px;
  margin-bottom: 25px;
`;

const Dot = styled.div`
  position: absolute;
  left: 0;
  top: 16px;
  width: 12px;
  height: 12px;
  background: #4f46e5;
  border-radius: 50%;
`;

const Content = styled.div`
  background: #f9fafb;
  border-radius: 10px;
  padding: 12px 15px;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Action = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #111827;
  display: flex;
  align-items: center;
`;


const Time = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const Meta = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #374151;
`;

const User = styled.span`
  font-weight: 500;
`;

const Role = styled.span`
  color: #6b7280;
  margin-left: 5px;
`;

const Changes = styled.div`
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed #e5e7eb;
`;

const ChangeRow = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  margin-top: 4px;
`;

const Field = styled.div`
  font-weight: 600;
  color: #374151;
  width: 120px;
`;

const OldValue = styled.div`
  color: #dc2626;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Arrow = styled.div`
  margin: 0 8px;
  color: #9ca3af;
`;

const NewValue = styled.div`
  color: #16a34a;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const EmptyState = styled.div`
  text-align: center;
  color: #6b7280;
  padding: 40px 0;
  font-size: 14px;
`;

const IconWrapper = styled.span`
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
  color: #4f46e5;
  font-size: 16px;
`;



const CaseActivityLog = ({ t, caseId, themeColors, closeModal, dispatch, navigate }) => {

    const [historyData, setHistoryData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchActivityLog = async () => {
            setIsLoading(true);
            try {
                const response = await getData({},`whistleblowing/${caseId}/activity-log`, dispatch, navigate);
                if (response.status === 200) {
                    setHistoryData(response.data.data);
                } else {
                    console.error("Error fetching activity log:", response.data);
                }
            } catch (error) {
                console.error("Error fetching activity log:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchActivityLog();
    }, [caseId]);


  return (
    <PopupView
      popupData={<HistoryView historyData={historyData} t={t} isLoading={isLoading} />}
      themeColors={themeColors}
      closeModal={closeModal}
      itemTitle={t("activityLog")}
      customClass={"medium"}
    />
  );
};

const actionIcons = {
  CASE_CREATED: <FiFileText color="#000000" />,
  STATUS_CHANGED: <FiRefreshCw color="#000000" />,
  LEVEL_CHANGED: <FiEdit color="#000000" />,
  DEADLINE_CHANGED: <FiClock color="#000000" />,
  HANDLER_ASSIGNED: <FiUserPlus color="#000000" />,
  HANDLER_CHANGED: <FiUserCheck color="#000000" />,
  COMMENT_ADDED: <FiMessageSquare color="#000000" />,
  MESSAGE_SENT: <FiMessageSquare color="#000000" />,
  FILE_UPLOADED: <FiFileText color="#000000" />,
  CASE_VIEWED: <FiEye color="#000000" />,
  CASE_CLOSED: <FiLock color="#000000" />,
  CASE_REOPENED: <FiUnlock color="#000000" />,
  FIELD_UPDATED: <FiEdit color="#000000" />,
  ACCESS_GRANTED: <FiUserPlus color="#000000" />,
  ACCESS_REVOKED: <FiUserCheck color="#000000" />,
};


const isValidDate = (value) => {
  if (!value) return false;
  if (value instanceof Date) return true;
  if (typeof value === "string" && !isNaN(Date.parse(value))) return true;
  return false;
};

const formatValue = (key, value, t) => {
  if (value === null || value === undefined) return "-";

  if (key === "assignedCaseHandler") {
    return value?.fullName || t("Unassigned");
  }

  if (isValidDate(value)) {
    return dateFormat(value);
  }

  if (typeof value === "boolean") {
    return value ? t("Yes") : t("No");
  }

  return t(String(value));
};


const HistoryView = ({ historyData, t, isLoading }) => {
  return (
    <TimelineContainer>
      {isLoading ? (
        <EmptyState style={{ fontSize: "18px" }}>{t("Loading...")}</EmptyState>
      ) : historyData.length === 0 ? (
        <EmptyState>{t("No activity found")}</EmptyState>
      ) : (
        historyData.map((entry, index) => (
          <TimelineItem key={index}>
            {/* <Dot /> */}
            <Content>
              <Header>
                <Action>
                  <IconWrapper>
                    {actionIcons[entry.action] || <FiEdit color="#000000" />}
                  </IconWrapper>
                  {t(entry.description) || t(entry.action)}
                </Action>
                <Time>{dateTimeFormat(entry.createdAt)}</Time>
              </Header>

              {entry.actor?.fullName && (
                <Meta>
                  {t("By")} <User>{entry.actor?.fullName || "System"}</User>
                  {entry.actorRole && entry.actorRole !== "user-defined" && (
                    <Role>({t(entry.actorRole)})</Role>
                  )}
                </Meta>
              )}

              {entry.changes && (
                <Changes>
                  {Object.keys(entry.changes.before || {}).map((key) => (
                    <ChangeRow key={key}>
                      <Field>{t(key)}</Field>
                      <OldValue>
                        {formatValue(key, entry.changes.before[key], t)}
                      </OldValue>
                      <Arrow>→</Arrow>
                      <NewValue>
                        {formatValue(key, entry.changes.after[key], t)}
                      </NewValue>
                    </ChangeRow>
                  ))}
                </Changes>
              )}
            </Content>
          </TimelineItem>
        ))
      )}
    </TimelineContainer>
  );
};


export default CaseActivityLog;
